import { get } from "lodash-es";
import { axiosInstance } from "../../../../context/axiosInstance";
import { MESApiService, spParameter } from "../../../../services";
import {
	Action,
	CacheConfiguration,
	QueryCacheConfigurationsResponse,
} from "../models/CacheConfiguration";
import { QueryCacheStats } from "../models/QueryCacheStats";

export const getQueryCacheConfigurations = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[SYSTEM].[GetQueryCacheConfigurations]",
		[{ name: "OnlyActive", value: false }],
		signal,
	);

	if (resp.ok) {
		const configurations = get(
			resp,
			"data.tables[0].rows",
			[],
		) as CacheConfiguration[];
		const actions = get(resp, "data.tables[1].rows", []) as Action[];

		return { configurations, actions } as QueryCacheConfigurationsResponse;
	}

	throw new Error(resp.message || "Error fetching query cache configurations");
};

export const upsertQueryCacheConfiguration = async ({
	actionId,
	ttlSeconds,
	isActive,
	comments,
}: {
	actionId: number;
	ttlSeconds: number;
	isActive: boolean;
	comments: string | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ActionId", value: actionId },
		{ name: "TTLSeconds", value: ttlSeconds },
		{ name: "IsActive", value: isActive },
		{ name: "Comments", value: comments },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[UpsertQueryCacheConfiguration]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error when saving the query cache configuration",
		);
	}
};

export const deleteQueryCacheConfiguration = async (actionId: number) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[SYSTEM].[DeleteQueryCacheConfiguration]",
		[{ name: "ActionId", value: actionId }],
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error when deleting the query cache configuration",
		);
	}
};

// How much the cache is holding right now. Not an SP either, so it goes straight to the endpoint.
export const getQueryCacheStats = async (signal?: AbortSignal) => {
	try {
		const resp = await axiosInstance.get("/cache/stats", { signal });
		return resp.data as QueryCacheStats;
	} catch (err: any) {
		throw new Error(
			err?.response?.data?.message ||
				err?.message ||
				"Error reading the query cache usage",
		);
	}
};

// Clears what is already stored for an action, without waiting for its TTL. Same endpoint the
// watcher service uses. Not an SP, so it does not go through callV2.
export const invalidateQueryCache = async (actionName: string) => {
	try {
		await axiosInstance.post("/cache", { ActionName: actionName });
	} catch (err: any) {
		throw new Error(
			err?.response?.data?.message ||
				err?.message ||
				"Error when clearing the query cache",
		);
	}
};
